// src/services/estimateService.js
import apiClient from './apiClient';

/**
 * Estimate service for API operations
 */
export const estimateService = {
  /**
   * Get all estimates with optional filters
   * @param {Object} filters - Optional filters (status, clientId, limit, offset)
   * @returns {Promise<Object>} Response with estimates array
   */
  getAllEstimates: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiClient.get(`/estimates?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch estimates');
    }
  },

  /**
   * Get a specific estimate by ID
   * @param {string|number} estimateId - The estimate ID
   * @returns {Promise<Object>} Response with estimate data
   */
  getEstimateById: async (estimateId) => {
    try {
      const response = await apiClient.get(`/estimates/${estimateId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Estimate not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch estimate');
    }
  },

  /**
   * Create a new estimate
   * @param {Object} estimateData - Estimate data
   * @returns {Promise<Object>} Response with created estimate
   */  createEstimate: async (estimateData) => {
    try {
      // Ensure financial values are formatted correctly for backend expectations
      const formattedData = {
        ...estimateData,
        subTotal: estimateData.subTotal?.toString() || '0',
        taxAmount: estimateData.taxAmount?.toString() || '0',
        totalAmount: estimateData.totalAmount?.toString() || '0',
        taxRate: parseFloat(estimateData.taxRate) || 0  // Keep as number for schema validation
      };
      
      const response = await apiClient.post('/estimates', formattedData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create estimate');
    }
  },

  /**
   * Update an existing estimate
   * @param {string|number} estimateId - The estimate ID
   * @param {Object} estimateData - Updated estimate data
   * @returns {Promise<Object>} Response with updated estimate
   */  updateEstimate: async (estimateId, estimateData) => {
    try {
      // Ensure financial values are formatted correctly for backend expectations
      const formattedData = {
        ...estimateData,
        subTotal: estimateData.subTotal?.toString() || '0',
        taxAmount: estimateData.taxAmount?.toString() || '0',
        totalAmount: estimateData.totalAmount?.toString() || '0',
        taxRate: parseFloat(estimateData.taxRate) || 0  // Keep as number for schema validation
      };
      
      const response = await apiClient.put(`/estimates/${estimateId}`, formattedData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update estimate');
    }
  },

  /**
   * Delete an estimate
   * @param {string|number} estimateId - The estimate ID
   * @returns {Promise<Object>} Response confirming deletion
   */
  deleteEstimate: async (estimateId) => {
    try {
      const response = await apiClient.delete(`/estimates/${estimateId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete estimate');
    }
  },

  /**
   * Accept an estimate (client action)
   * @param {string|number} estimateId - The estimate ID
   * @returns {Promise<Object>} Response with updated estimate
   */
  acceptEstimate: async (estimateId) => {
    try {
      const response = await apiClient.post(`/estimates/${estimateId}/accept`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to accept estimate');
    }
  },

  /**
   * Reject an estimate (client action)
   * @param {string|number} estimateId - The estimate ID
   * @returns {Promise<Object>} Response with updated estimate
   */
  rejectEstimate: async (estimateId) => {
    try {
      const response = await apiClient.post(`/estimates/${estimateId}/reject`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject estimate');
    }
  },
  /**
   * Convert an estimate to an invoice (admin/staff action)
   * @param {string|number} estimateId - The estimate ID
   * @returns {Promise<Object>} Response with the new invoice data
   */
  convertToInvoice: async (estimateId) => {
    try {
      const response = await apiClient.post(`/estimates/${estimateId}/convert-to-invoice`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to convert estimate to invoice');
    }
  }
};
